import { Box, Button, Chip, CircularProgress } from '@mui/material'
import images from 'assets/images'
import axios from 'providers/axios'
import { KEYS, URL_APP, URL_HOME } from 'helpers/config'
import utils from 'helpers/utils'
import useReCaptcha from 'hooks/useReCaptcha'
import useShowError from 'hooks/useShowError'
import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import TextFieldNN from 'components/TextFieldNN'
import PasswordInput from 'components/PasswordInput'

const SignUpSellerForm: React.FC = () => {
  const showError = useShowError()
  const { reCaptchaLoaded, generateReCaptchaToken } = useReCaptcha()

  const [firstName, setFirstName] = useState({ value: '', error: false, touched: false })
  const [lastName, setLastName] = useState({ value: '', error: false, touched: false })
  const [companieName, setCompanieName] = useState({ value: '', error: false, touched: false })
  const [email, setEmail] = useState({ value: '', error: false, touched: false })
  const [password, setPassword] = useState({ value: '', error: false })

  const [loading, setLoading] = useState(false)

  const validateFirstName = useCallback(() => {
    const isValid = firstName.value.length > 0
    setFirstName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [firstName.value])

  const validateLastName = useCallback(() => {
    const isValid = lastName.value.length > 0
    setLastName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [lastName.value])

  const validateCompanieName = useCallback(() => {
    const isValid = companieName.value.length > 0
    setCompanieName((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [companieName.value])

  const validateEmail = useCallback(() => {
    const isValid = utils.isValidEmail(email.value)
    setEmail((message) => ({ ...message, error: !isValid }))
    return isValid
  }, [email.value])

  const isFormValid = useMemo(() => {
    const validFirstName = validateFirstName()
    const validLastName = validateLastName()
    const validCompanieName = validateCompanieName()
    const validEmail = validateEmail()
    const validPassword = !password.error
    return validFirstName && validLastName && validCompanieName && validEmail && validPassword
  }, [validateFirstName, validateLastName, validateCompanieName, validateEmail, password.error])

  const onSubmit = () => {
    const signUp = async () => {
      if (!isFormValid) return
      setLoading(true)
      try {
        const authDevice = utils.getFullAuthDeviceData()
        const token = await generateReCaptchaToken('signUpSeller')
        const { data } = await axios.post(
          '/sign-up/seller',
          {
            firstName: firstName.value,
            lastName: lastName.value,
            companieName: companieName.value,
            email: email.value,
            password: password.value,
            authDevice,
          },
          {
            headers: {
              [KEYS.RECAPTCHA_TOKEN]: token,
              [KEYS.RECAPTCHA_ACTION]: 'signUpSeller',
            },
          }
        )
        if (data) {
          const authDevice = data.authDevice
          utils.setChromeExtension(authDevice.deviceToken)
          window.location.href = URL_APP
          return
        }
      } catch (error) {
        showError(error)
        setLoading(false)
      }
    }

    signUp()
  }

  return (
    <Box width="100%" maxWidth="350px" mx="auto" textAlign="center">
      <Box textAlign="center">
        <img src={images.LOGO_NN_BLUE} alt="logo nachonacho seller" style={{ width: 290 }} />
        <Chip
          size="small"
          sx={{ bgcolor: 'var(--dark-blue)', mt: '10px', px: '10px', '& > span': { color: 'white' } }}
          label="Seller Station"
        />
      </Box>

      <Box height="24px" />

      <Box>
        <span className="fs-14">
          Already have an account?{' '}
          <Link to={`/login${window.location.search}`} className="link">
            Login
          </Link>
        </span>
      </Box>

      <Box height="24px" />

      <Box display="flex" gap="8px">
        <TextFieldNN
          id="first-name"
          label="First name"
          placeholder="John"
          value={firstName.value}
          onChange={(e) => setFirstName((state) => ({ ...state, value: e.target.value, error: false }))}
          onFocus={() => setFirstName((state) => ({ ...state, touched: false }))}
          onBlur={() => setFirstName((state) => ({ ...state, touched: true }))}
          error={firstName.touched && firstName.error}
          helperText="First name is required"
        />
        <TextFieldNN
          id="last-name"
          label="Last name"
          placeholder="Doe"
          value={lastName.value}
          onChange={(e) => setLastName((state) => ({ ...state, value: e.target.value, error: false }))}
          onFocus={() => setFirstName((state) => ({ ...state, touched: false }))}
          onBlur={() => setLastName((state) => ({ ...state, touched: true }))}
          error={lastName.touched && lastName.error}
          helperText="Last name is required"
        />
      </Box>

      <TextFieldNN
        id="company-name"
        label="Company legal name"
        placeholder="Acme Corp"
        value={companieName.value}
        onChange={(e) => setCompanieName((state) => ({ ...state, value: e.target.value, error: false }))}
        onFocus={() => setCompanieName((state) => ({ ...state, touched: false }))}
        onBlur={() => setCompanieName((state) => ({ ...state, touched: true }))}
        error={companieName.touched && companieName.error}
        helperText="Company name is required"
      />

      <TextFieldNN
        id="email"
        label="Email"
        type="email"
        placeholder="example@address.com"
        value={email.value}
        onChange={(e) => setEmail((state) => ({ ...state, value: e.target.value, error: false }))}
        onFocus={() => setEmail((state) => ({ ...state, touched: false }))}
        onBlur={() => setEmail((state) => ({ ...state, touched: true }))}
        error={email.touched && email.error}
        helperText={email.value.length > 0 ? 'Email is invalid' : 'Email is required'}
      />

      <PasswordInput
        label="Password"
        onChange={(state) => {
          setPassword({
            value: state.value,
            error: !state.isValid,
          })
        }}
      />

      <Button
        variant="contained"
        fullWidth
        disabled={!isFormValid || !reCaptchaLoaded || loading}
        onClick={onSubmit}
        sx={{ mt: '16px' }}
      >
        {loading ? <CircularProgress size="20px" /> : 'Sign up'}
      </Button>

      <Box maxWidth="265px" mx="auto" mt="24px">
        <span className="fs-14">
          By signing up, you agree to NachoNacho's{' '}
          <a href={`${URL_HOME}/terms-of-use`} className="link">
            Terms of Use
          </a>{' '}
          and{' '}
          <a href={`${URL_HOME}/privacy-policy`} className="link">
            Privacy Policy
          </a>
        </span>
      </Box>
    </Box>
  )
}

export default SignUpSellerForm
